import type { Service, Customer, ContactRequest, DashboardStats, MediaFile, AdminUser, Device, DeviceInput } from '../types/api.types';
import api from './api';

// ── Services (API-backed) ─────────────────────────────────────────────────────

export async function getServices(): Promise<Service[]> {
  const res = await api.get<Service[]>('/services');
  return res.data;
}

export async function getAdminServices(): Promise<Service[]> {
  const res = await api.get<Service[]>('/admin/services');
  return res.data;
}

export async function getServiceById(id: string | number): Promise<Service | null> {
  const services = await getServices();
  return services.find((s) => String(s.id) === String(id)) ?? null;
}

export async function saveService(data: Omit<Service, 'id' | 'order_index' | 'is_active'>): Promise<Service> {
  const res = await api.post<Service>('/admin/services', data);
  return res.data;
}

export async function updateService(id: string | number, data: Partial<Service>): Promise<Service> {
  const res = await api.put<Service>(`/admin/services/${id}`, data);
  return res.data;
}

export async function deleteService(id: string | number): Promise<void> {
  await api.delete(`/admin/services/${id}`);
}

// ── Customers (API-backed) ────────────────────────────────────────────────────

export async function getCustomers(): Promise<Customer[]> {
  const res = await api.get<Customer[]>('/customers');
  return res.data;
}

function toFormData(data: Record<string, unknown>, file?: File): FormData {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => { if (v != null) fd.append(k, String(v)); });
  if (file) fd.append('logo_file', file);
  return fd;
}

// Multipart config: remove Content-Type so browser sets multipart/form-data with boundary
const multipartHeaders = { headers: { 'Content-Type': false as unknown as string } };

export async function saveCustomer(data: Omit<Customer, 'id'>, logoFile?: File): Promise<Customer> {
  if (logoFile) {
    const res = await api.post<Customer>('/admin/customers', toFormData(data as Record<string, unknown>, logoFile), multipartHeaders);
    return res.data;
  }
  const res = await api.post<Customer>('/admin/customers', data);
  return res.data;
}

export async function updateCustomer(id: string | number, data: Partial<Customer>, logoFile?: File): Promise<Customer> {
  if (logoFile) {
    const fd = toFormData(data as Record<string, unknown>, logoFile);
    fd.append('_method', 'PUT');
    const res = await api.post<Customer>(`/admin/customers/${id}`, fd, multipartHeaders);
    return res.data;
  }
  const res = await api.put<Customer>(`/admin/customers/${id}`, data);
  return res.data;
}

export async function deleteCustomer(id: string | number): Promise<void> {
  await api.delete(`/admin/customers/${id}`);
}

// ── Contacts (API-backed) ─────────────────────────────────────────────────────

export async function getContacts(): Promise<ContactRequest[]> {
  const res = await api.get<ContactRequest[]>('/admin/contacts');
  return res.data;
}

export async function saveContact(data: Omit<ContactRequest, 'id' | 'status' | 'created_at'>): Promise<void> {
  await api.post('/contact', data);
}

export async function updateContactStatus(id: string | number, status: ContactRequest['status']): Promise<void> {
  await api.put(`/admin/contacts/${id}`, { status });
}

export async function deleteContact(id: string | number): Promise<void> {
  await api.delete(`/admin/contacts/${id}`);
}

// ── Lohnbuchhaltung-Preise ────────────────────────────────────────────────────

/** Returns a map of { labelKey: price } from the DB */
export async function getPrices(): Promise<Record<string, string>> {
  const res = await api.get<Record<string, string>>('/prices');
  return res.data;
}

/** Bulk-upserts all price items */
export async function updatePrices(items: { label_key: string; price: string }[]): Promise<void> {
  await api.put('/admin/prices', { items });
}

// ── Site Content ───────────────────────────────────────────────────────────────
export async function getSiteContent(key: string): Promise<Record<string, unknown>> {
  const res = await api.get<Record<string, unknown>>(`/content/${key}`);
  return res.data ?? {};
}

export async function updateSiteContent(key: string, content: Record<string, unknown>): Promise<void> {
  await api.put(`/admin/content/${key}`, { content });
}

// ── Site Content (Admin) ──────────────────────────────────────────────────────

export interface SiteContentMeta { key: string; updated_at: string | null; }

export async function getSiteContentList(): Promise<SiteContentMeta[]> {
  const res = await api.get<SiteContentMeta[]>('/admin/content');
  return res.data;
}

export async function resetSiteContent(key: string): Promise<void> {
  await api.delete(`/admin/content/${key}`);
}

// ── Dashboard Stats ───────────────────────────────────────────────────────────

export async function getDashboardStats(): Promise<DashboardStats> {
  const res = await api.get<DashboardStats>('/admin/stats');
  return res.data;
}

// ── Medien ────────────────────────────────────────────────────────────────────

export type UploadFolder = 'team' | 'media';

export async function uploadFile(file: File, folder: UploadFolder = 'media'): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('folder', folder);
  const res = await api.post<{ url: string }>('/admin/upload-image', fd, multipartHeaders);
  return res.data.url;
}

export async function uploadTeamImage(file: File): Promise<string> {
  return uploadFile(file, 'team');
}

export async function getMedia(): Promise<MediaFile[]> {
  const res = await api.get<MediaFile[]>('/admin/media');
  return res.data;
}

export async function deleteMedia(file: MediaFile): Promise<void> {
  await api.delete(`/admin/media/${file.folder}/${encodeURIComponent(file.name)}`);
}

// ── Admin-Benutzer ────────────────────────────────────────────────────────────

export async function getUsers(): Promise<AdminUser[]> {
  const res = await api.get<AdminUser[]>('/admin/users');
  return res.data;
}

export async function createUser(data: { name: string; email: string; password: string }): Promise<AdminUser> {
  const res = await api.post<AdminUser>('/admin/users', data);
  return res.data;
}

export async function updateUser(id: number, data: { name?: string; email?: string; password?: string }): Promise<AdminUser> {
  const res = await api.put<AdminUser>(`/admin/users/${id}`, data);
  return res.data;
}

export async function deleteUser(id: number): Promise<void> {
  await api.delete(`/admin/users/${id}`);
}

export async function changeOwnPassword(data: { current_password: string; password: string; password_confirmation: string }): Promise<void> {
  await api.put('/auth/password', data);
}

// ── Geräte ────────────────────────────────────────────────────────────────────

export async function getDevices(): Promise<Device[]> {
  const res = await api.get<Device[]>('/admin/devices');
  return res.data;
}

/** Einzelnes Gerät inklusive Zugangsdaten */
export async function getDevice(id: number): Promise<Device> {
  const res = await api.get<Device>(`/admin/devices/${id}`);
  return res.data;
}

export async function createDevice(data: Partial<DeviceInput>): Promise<Device> {
  const res = await api.post<Device>('/admin/devices', data);
  return res.data;
}

export async function updateDevice(id: number, data: Partial<DeviceInput>): Promise<Device> {
  const res = await api.put<Device>(`/admin/devices/${id}`, data);
  return res.data;
}

export async function deleteDevice(id: number): Promise<void> {
  await api.delete(`/admin/devices/${id}`);
}

export async function importDevices(devices: Partial<DeviceInput>[]): Promise<{ created: number; ids: number[]; errors: { row: number; errors: string[] }[] }> {
  const res = await api.post('/admin/devices/import', { devices });
  return res.data;
}

export async function uploadDeviceFile(id: number, file: File): Promise<Device> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await api.post<Device>(`/admin/devices/${id}/files`, fd, multipartHeaders);
  return res.data;
}

export async function deleteDeviceFile(id: number, fileId: string): Promise<Device> {
  const res = await api.delete<Device>(`/admin/devices/${id}/files/${fileId}`);
  return res.data;
}

/** Lädt eine private Gerätedatei mit Login-Token und startet den Download */
export async function downloadDeviceFile(id: number, fileId: string, name: string): Promise<void> {
  const res = await api.get(`/admin/devices/${id}/files/${fileId}`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Liest die erste Fehlermeldung aus einer Laravel-Antwort */
export function apiErrorMessage(err: unknown, fallback = 'Es ist ein Fehler aufgetreten.'): string {
  const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
  const first = data?.errors ? Object.values(data.errors)[0]?.[0] : undefined;
  return first ?? data?.message ?? fallback;
}
